import { default as useUserPermissions } from '@consolidocs-ui/hooks/useUserPermissions';
import { useEffect, useState } from 'react';
import { useAuth } from 'react-oidc-context';
import { RouteObject } from 'react-router';

const routes = import.meta.glob('../routes/*.routes.tsx');

const useRoutes = (): { routes: RouteObject[]; isLoading: boolean } => {
  const auth = useAuth();
  const [roleRoutes, setRoleRoutes] = useState<RouteObject[]>([]);
  // Key of the auth/role state the current roleRoutes were resolved for.
  // isLoading is derived from it during render, so the moment auth or role
  // changes we report loading again *in the same render* — closing the gap
  // (render happens before effects) that used to flash Access Denied on a
  // hard refresh while the Cognito session was still being restored.
  const [resolvedKey, setResolvedKey] = useState<string | null>(null);
  let {
    roleFile: role,
    isFetching,
    isCompanyAdmin,
    error,
  } = useUserPermissions();

  if (isCompanyAdmin) {
    role = 'company-admin';
  }

  const loadKey = `${auth.isAuthenticated}|${role ?? ''}|${error ? 'err' : ''}`;

  useEffect(() => {
    // Session still being restored (e.g. hard refresh) — defer any decision.
    if (auth.isLoading) return;

    let cancelled = false;
    const finish = (loaded: RouteObject[]) => {
      if (cancelled) return;
      setRoleRoutes(loaded);
      setResolvedKey(loadKey);
    };

    if (!auth.isAuthenticated || error) {
      finish([]);
      return;
    }

    const loadRoutes = async () => {
      const routePath = `../routes/${role}.routes.tsx`;

      if (!routes[routePath]) {
        finish([]);
        return;
      }

      try {
        const module = (await routes[routePath]()) as {
          default: RouteObject[];
        };

        const loadedRoutes = module.default || [];

        // Deduplicate by `path`, keeping the last occurrence
        const seen = new Map<string, RouteObject>();
        for (let i = loadedRoutes.length - 1; i >= 0; i--) {
          const route = loadedRoutes[i];
          const key = route.path || `__index__${i}`; // fallback for routes without a path
          if (!seen.has(key)) {
            seen.set(key, route);
          }
        }

        // Reverse to restore original order before deduplication
        const deduplicatedRoutes = Array.from(seen.values()).reverse();

        // ✅ Sort routes by `sort` key, placing undefined `sort` at the end
        deduplicatedRoutes.sort((a: any, b: any) => {
          const sortA = typeof a.sort === 'number' ? a.sort : Infinity;
          const sortB = typeof b.sort === 'number' ? b.sort : Infinity;
          return sortA - sortB;
        });

        finish(deduplicatedRoutes);
      } catch (e) {
        console.error(`[useRoutes] failed to load ${routePath}`, e);
        finish([]);
      }
    };

    loadRoutes();

    // Ignore results from a stale load if auth/role changed mid-flight.
    return () => {
      cancelled = true;
    };
  }, [loadKey, auth.isLoading, auth.isAuthenticated, role, error]);

  return {
    routes: roleRoutes,
    // Loading until the session is restored, permissions have arrived AND the
    // routes for the *current* auth/role state have been resolved.
    isLoading: auth.isLoading || isFetching || resolvedKey !== loadKey,
  };
};

export default useRoutes;
